import { useEffect } from 'react'

// G2 — `?section=kop` / `?section=logo` pada halaman Pengaturan Desa.
//
// Kotak info di modul Template Surat mengarahkan petugas langsung ke bagian yang dituju,
// bukan ke puncak halaman pengaturan yang panjang. Dipakai oleh KEDUA shell
// (staff desa & platform admin), jadi logikanya di sini — bukan digandakan di dua route.

/** Nama bagian yang boleh dituju lewat `?section=`. */
export const SECTION_PENGATURAN = ['kop', 'logo'] as const

export type SectionPengaturan = (typeof SECTION_PENGATURAN)[number]

/** `?section=` hanya diterima bila cocok dengan daftar di atas — selain itu diabaikan. */
export function bacaSectionParam(nilai: unknown): SectionPengaturan | undefined {
  return SECTION_PENGATURAN.find((s) => s === nilai)
}

/** Id elemen DOM untuk satu bagian pengaturan. */
export function idSectionPengaturan(nama: SectionPengaturan): string {
  return `pengaturan-section-${nama}`
}

/**
 * URL halaman pengaturan beserta `?section=`, dihitung dari pathname yang sedang terbuka.
 *
 * Kedua route `template-surat` (shell staff desa & shell platform) bersebelahan dengan route
 * `pengaturan`-nya, jadi mengganti segmen terakhir cukup — pemanggil tidak perlu tahu shell mana
 * yang aktif maupun slug/desaId-nya. `null` bila dipanggil dari pathname lain, agar tautan yang
 * salah arah tidak pernah dirender.
 */
export function urlPengaturanSection(pathname: string, section: SectionPengaturan): string | null {
  const bersih = pathname.replace(/\/$/, '')
  if (!bersih.endsWith('/template-surat')) return null
  return `${bersih.replace(/\/template-surat$/, '/pengaturan')}?section=${section}`
}

/** Batas waktu menunggu section muncul DAN posisinya berhenti bergeser, sebelum menyerah. */
export const BATAS_TUNGGU_SECTION_MS = 4000

/** Jeda antar percobaan mencari dan meluruskan posisi section. */
export const JEDA_COBA_SECTION_MS = 120

/** Jarak section dari tepi atas viewport — menyisakan ruang untuk bilah atas yang menempel. */
export const JARAK_ATAS_SECTION_PX = 80

/** Selisih piksel yang masih dianggap "sudah sampai" pada posisi target. */
export const TOLERANSI_POSISI_PX = 4

/** Posisi dianggap sudah pas bila jarak section dari tepi atas viewport mendekati target. */
export function sudahDiPosisi(jarakDariAtas: number): boolean {
  return Math.abs(jarakDariAtas - JARAK_ATAS_SECTION_PX) <= TOLERANSI_POSISI_PX
}

/**
 * Gulir ke bagian pengaturan yang dituju `?section=`.
 *
 * Dua hal membuat satu kali `scrollIntoView` saat mount tidak cukup:
 * elemennya belum ada (halaman menampilkan skeleton sampai data desa siap), dan setelah ada pun
 * posisinya masih bergeser karena section di atasnya (Copy Config, Role Menu, Penomoran) memuat
 * datanya sendiri dan menambah tinggi halaman. Karena itu posisi diluruskan berulang sampai pas
 * atau waktu habis. Begitu petugas menggulir sendiri, pelurusan berhenti — jangan berebut kemudi.
 */
export function useScrollToSection(section: SectionPengaturan | undefined): void {
  useEffect(() => {
    if (!section) return

    let dibatalkan = false
    let timer = 0
    const id = idSectionPengaturan(section)
    const mulai = Date.now()

    const coba = () => {
      if (dibatalkan) return
      const el = document.getElementById(id)
      const jarak = el?.getBoundingClientRect().top

      if (jarak !== undefined && sudahDiPosisi(jarak)) return
      if (jarak !== undefined) {
        // Seketika, bukan `smooth`: dengan animasi, pengukuran pada tik berikutnya masih
        // menangkap posisi di tengah jalan sehingga koreksi menumpuk dan berakhir melewati sasaran.
        window.scrollTo({ top: window.scrollY + jarak - JARAK_ATAS_SECTION_PX })
      }
      if (Date.now() - mulai > BATAS_TUNGGU_SECTION_MS) return
      timer = window.setTimeout(coba, JEDA_COBA_SECTION_MS)
    }

    const batalkan = () => {
      dibatalkan = true
    }
    const interaksi = ['wheel', 'touchstart', 'keydown'] as const
    for (const nama of interaksi) window.addEventListener(nama, batalkan, { passive: true })

    coba()

    return () => {
      dibatalkan = true
      window.clearTimeout(timer)
      for (const nama of interaksi) window.removeEventListener(nama, batalkan)
    }
  }, [section])
}
